/* Not sure putting this into a composable is the best solution. It's being used
 * when cubes are received for the global cubes list and the tour cubes list.
 * Since all this data should be set in the backend in the first place this
 * should be eliminated altogether.
 */
import { useDistance } from '@/composables/useDistance.ts'
import type { CubeMapData } from '@/types/CubeDto'
import { BooleanUtil } from '@/util/BooleanUtil'
import { NumberUtil } from '@/util/NumberUtil'

export function usePreprocessCube() {
  const { getDistance } = useDistance()

  /**
   * Apply any changes/normalization needed to a cube object so we can safely
   * and consistently use it in the app.
   * This includes type changes, like converting "1" and 1 to boolean values
   *
   * @param cube The cube to preprocess, this is not the selected cube.
   * @param homeCoordinates The coordinates of the selected cube this app uses.
   */
  function preprocessCube(cube: CubeMapData, homeCoordinates: any) {
    if (!cube.params.cubeName) {
      console.warn("Filtering cube without cubeName", cube);

      return undefined
    }

    cube.cubeName = cube.params.cubeName

    // The backend can return either '1' or true as positive values.
    cube.hasConsumption = BooleanUtil.isTrue(cube.params.hasConsumption)
    cube.hasProduction = BooleanUtil.isTrue(cube.params.hasProduction)

    if (cube.hasProduction && cube.params?.productionType) {
      cube.productionType = cube.params?.productionType?.toLowerCase()
    }

    let connection = cube.connectionMetadata
    cube.isMyEfriend = cube.connectionStatus && cube.connectionStatus !== 'none'
    connection.isProducer = BooleanUtil.isTrue(connection.isProducer)
    connection.isConsumer = BooleanUtil.isTrue(connection.isConsumer)
    connection.donationConnection = BooleanUtil.isTrue(connection.donationConnection)

    cube.isContributor =
    connection.donationConnection &&
    connection.isProducer &&
    connection.donator === cube.params.cubeName

    cube.params.openConnectionsAvailable = NumberUtil.parse(cube.params.openConnectionsAvailable);
    cube.params.energyLinesRequired = NumberUtil.parse(cube.params.energyLinesRequired);
    cube.params.latitude = NumberUtil.parse(cube.params.latitude);
    cube.params.longitude = NumberUtil.parse(cube.params.longitude);
    cube.params.pricePerKWhNet = NumberUtil.parse(cube.params.pricePerKWhNet);
    cube.params.kWhPriceNet = NumberUtil.parse(cube.params.kWhPriceNet);
    cube.params.productionCapacityKWp = NumberUtil.parse(cube.params.productionCapacityKWp);
    cube.params.hasPV = BooleanUtil.isTrue(cube.params.hasPV);
    cube.params.allowDisplayProductionDetails = BooleanUtil.isTrue(cube.params.allowDisplayProductionDetails);
    cube.params.hasChargingStation = BooleanUtil.isTrue(cube.params.hasChargingStation);

    cube.isDonatee =
      connection.donationConnection &&
      connection.isConsumer &&
      connection.donatee === cube.params.cubeName

    cube.isProducer =
      cube.connectionStatus === 'producer' ||
      cube.connectionStatus === 'bidirectional'

    cube.isConsumer =
      cube.connectionStatus === 'consumer' ||
      cube.connectionStatus === 'bidirectional'

    cube.canReceiveDonations = BooleanUtil.isTrue(cube.params.isDonationRecipient);

    cube.distance = getDistance(homeCoordinates, {
      lat: cube.params.latitude,
      lng: cube.params.longitude,
    })

    cube.numLikes = cube.votingInformation ? cube.votingInformation.numLikes : 0

    // After discussing this with Thomas Dietmaier, the backend sends '-' as string in case grid
    // fee discount does not apply.
    cube.gridFeeDiscountApplies = cube.hasOwnProperty('netzkostenersparnis') && cube.netzkostenersparnis !== '-'

    return cube
  }

  return {
    preprocessCube,
  }
}
